// WebSocket bağlantısı: kopunca artan beklemelerle yeniden bağlanır, her bağlantıda sunucu
// tüm ders durumunu (init) yeniden gönderir; böylece kopma sırasında kaçan hiçbir şey kalmaz.
import type { ClientMsg, HelloMsg, ServerMsg } from '../../shared/protocol';

export type ConnStatus = 'connecting' | 'online' | 'offline' | 'fatal';

export class LessonConnection {
  private ws: WebSocket | null = null;
  private closed = false;
  private attempt = 0;
  private retryTimer: number | null = null;

  constructor(
    private readonly hello: HelloMsg,
    private readonly onMessage: (msg: ServerMsg) => void,
    private readonly onStatus: (status: ConnStatus, detail?: string) => void,
  ) {
    this.open();
  }

  private open() {
    if (this.closed) return;
    this.onStatus(this.attempt === 0 ? 'connecting' : 'offline');
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${location.host}/ws`);
    this.ws = ws;
    ws.onopen = () => ws.send(JSON.stringify(this.hello));
    ws.onmessage = (ev) => {
      let msg: ServerMsg;
      try {
        msg = JSON.parse(String(ev.data)) as ServerMsg;
      } catch {
        return;
      }
      if (msg.t === 'init') {
        this.attempt = 0;
        this.onStatus('online');
      } else if (msg.t === 'fatal') {
        this.closed = true;
        this.onStatus('fatal', msg.reason);
      }
      this.onMessage(msg);
    };
    ws.onclose = (ev) => {
      if (this.ws === ws) this.ws = null;
      if (this.closed) return;
      if (ev.code >= 4400 && ev.code < 4500) {
        this.closed = true;
        this.onStatus('fatal', 'Bağlantı sunucu tarafından reddedildi.');
        return;
      }
      this.onStatus('offline');
      const delay = Math.min(8000, 400 * 2 ** this.attempt++) * (0.75 + Math.random() * 0.5);
      this.retryTimer = window.setTimeout(() => this.open(), delay);
    };
  }

  /** Bağlı değilse false döner; çağıran iyimser güncellemeyi yapmamalıdır. */
  send(msg: ClientMsg): boolean {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    ws.send(JSON.stringify(msg));
    return true;
  }

  close() {
    this.closed = true;
    if (this.retryTimer !== null) clearTimeout(this.retryTimer);
    this.ws?.close();
    this.ws = null;
  }
}
